/**
 * @file boardroomRecorderUiPreview.ts
 * @brief Dev-only jump to the live boardroom recording UI without mic/billing.
 *
 * @details
 * Toggle BOARDROOM_RECORDER_UI_PREVIEW to false before release. When true (or
 * `?uiPreview=1` on `/meetings/record`), BoardroomRecorderPage renders mock
 * session chrome so the mic glow / hold / cost UI can be styled without
 * starting capture.
 *
 * Mirrors FRONTEND MeetingRecorder `MEETING_RECORDER_PREVIEW_SCREEN`.
 *
 * @author DES
 * @date 28-07-2026
 */

/** Off for live. Use `?uiPreview=1` anytime to restyle without mic/billing. */
export const BOARDROOM_RECORDER_UI_PREVIEW = false;

/**
 * Soft gradient card chrome around the recorder.
 * Off for now (content sits on page canvas). Flip true anytime to restore.
 */
export const BOARDROOM_RECORDER_SHOW_CARD = false;

/** Grey soft wash used when `BOARDROOM_RECORDER_SHOW_CARD` is true. */
export const BOARDROOM_RECORDER_CARD_BACKGROUND =
  "radial-gradient(ellipse 120% 80% at 50% -10%, hsl(var(--foreground) / 0.06), transparent 55%), " +
  "radial-gradient(ellipse 90% 60% at 100% 100%, hsl(var(--foreground) / 0.04), transparent 50%), " +
  "linear-gradient(165deg, hsl(var(--secondary)) 0%, hsl(var(--card)) 42%, hsl(var(--muted)) 100%)";

export const BOARDROOM_RECORDER_PREVIEW_TITLE = "Web Meeting No.1";

export const BOARDROOM_RECORDER_PREVIEW_PARTICIPANTS = [
  {
    name: "Alex Chen",
    email: "alex@acme.com",
    profileImageUrl: undefined as string | undefined,
  },
  {
    name: "Sara Khan",
    email: "sara@acme.com",
    profileImageUrl: undefined as string | undefined,
  },
  {
    name: "Jordan Lee",
    email: "jordan@acme.com",
    profileImageUrl: undefined as string | undefined,
  },
  {
    name: "Priya Nair",
    email: "priya@acme.com",
    profileImageUrl: undefined as string | undefined,
  },
  {
    name: "Omar Farooq",
    email: "omar@acme.com",
    profileImageUrl: undefined as string | undefined,
  },
  {
    name: "Mia Rossi",
    email: "mia@acme.com",
    profileImageUrl: undefined as string | undefined,
  },
  {
    name: "Kenji Sato",
    email: "kenji@acme.com",
    profileImageUrl: undefined as string | undefined,
  },
  {
    name: "Elena Petrova",
    email: "elena@acme.com",
    profileImageUrl: undefined as string | undefined,
  },
];

export const BOARDROOM_RECORDER_PREVIEW_BOOKMARKS = [
  {
    id: "preview-decision-1",
    type: "decision" as const,
    timeInSecs: 23,
    timestamp: "00:23",
  },
  {
    id: "preview-todo-1",
    type: "todo" as const,
    timeInSecs: 26,
    timestamp: "00:26",
  },
  {
    id: "preview-decision-2",
    type: "decision" as const,
    timeInSecs: 28,
    timestamp: "00:28",
  },
  {
    id: "preview-todo-2",
    type: "todo" as const,
    timeInSecs: 29,
    timestamp: "00:29",
  },
  {
    id: "preview-todo-3",
    type: "todo" as const,
    timeInSecs: 34,
    timestamp: "00:34",
  },
  {
    id: "preview-decision-3",
    type: "decision" as const,
    timeInSecs: 41,
    timestamp: "00:41",
  },
  {
    id: "preview-todo-4",
    type: "todo" as const,
    timeInSecs: 48,
    timestamp: "00:48",
  },
  {
    id: "preview-decision-4",
    type: "decision" as const,
    timeInSecs: 55,
    timestamp: "00:55",
  },
];

/** Fake elapsed ms so the mic timer chrome looks live. */
export const BOARDROOM_RECORDER_PREVIEW_ELAPSED_MS = 36_000;

export const BOARDROOM_RECORDER_PREVIEW_MIME = "audio/webm;codecs=opus";

/**
 * Dock on? ONE rule for the recorder page and the desktop widget (DES
 * 11-10-2026: in the desktop app the widget was the new dock but the meeting
 * page still the old sheet). The dock is the standard since W10 (22-10-2026,
 * DES: final with the desktop widget): on everywhere, every window size.
 * Local dev only: `?dock=0` brings back the old dial sheet to compare.
 */
export function recorderDockOn(): boolean {
  if (!import.meta.env.DEV) return true;
  return new URLSearchParams(window.location.search).get("dock") !== "0";
}
